import {
  Link,
  useCanGoBack,
  useNavigate,
  useParams,
  useRouter,
} from '@tanstack/react-router';
import { format, parse } from 'date-fns';
import { CircleArrowLeft, Loader2 } from 'lucide-react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { useGetClass, useUpdateClassMutation } from '~entities/classes';
import { invalidateAfterClassUpdate } from '~entities/classes/api/update-class';
import { getHourlyTuitionBillingId } from '~entities/classes/helpers/get-hourly-tuition-billing-id';
import { getClassCountTuitionBillingId } from '~entities/classes/helpers/get-class-count-tuition-billing-id';
import { getClassUpdateApiError } from '~entities/classes/lib/class-update-error';
import { useGetClassCountTuitionBilling } from '~entities/payment/api/class-count-tuition/get-class-count-tuition-billing';
import { useGetHourlyTuitionBilling } from '~entities/payment/api/hourly-tuition/get-hourly-tuition-billing';
import { useGetSessionById } from '~entities/session/api/get-session-by-id';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import {
  AGE_RANGE_REVERSED_MESSAGE,
  calculateDurationInMinutes,
  fieldErrorMessage,
  formatDateTime,
  hasAgeRangeError,
  isValidTimeRange,
  normalizeTime,
  validateAgeRange,
} from '~features/classes/ui/helpers';
import { PrivateLessonOfferingSection } from '~features/classes/ui/private-lesson-offering-section';
import { CoverImageSelector } from '~features/shared/cover-image-selector';
// import { useGetAllSessionsOld } from '~entities/session';

import { LocationSelector } from '~features/shared/location-selector';
import { PolicySelector } from '~features/shared/policy-selector';
import { SessionSelector } from '~features/shared/session-selector';
import { TimePickerDropdown } from '~features/shared/time-picker-dropdown';

import { DateTimePicker } from '~shared/components/date-time-picker';
import { MAX_AGE_YEARS } from '~shared/constants/age';
import { TuitionBillingCycle } from '~shared/constants/tuition-billing-cycle';
import { extractDateOnly } from '~shared/helpers/datetime';
import { useUnsavedChangesBlocker } from '~shared/hooks/use-unsaved-changes-blocker';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { Switch } from '~shared/shadcn/ui/switch';
import { calculateDateDifference } from '~shared/utils/date-time';

import { GroupsSelector } from '../../shared/groups-selector';
import { InstructorSelector } from '../../shared/instructor-selector';
import { RoomSelector } from '../../shared/room-selector';
import { TagsSelector } from '../../shared/tags-selector';
import { ClassColorPicker } from './class-color-picker';
import { ClassPublicShareLinkSection } from './class-public-share-link-section';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
type Days = (typeof DAYS)[number];

interface FormData {
  classInfo: {
    className: string;
    location: {
      label: string | null;
      value: string | null;
    };
    room: {
      label: string | null;
      value: string | null;
    };
    instructor: Array<{
      label: string;
      value: string;
    }>;
    session: {
      label: string;
      value: string;
    };
    seats: number;
    minAge: {
      year: number;
      month: number;
    };
    maxAge: {
      year: number;
      month: number;
    };
    hide: boolean;
    color: string;
  };
  dates: {
    startDate: string;
    endDate: string;
    registrationStartDate: string;
  };
  days: Array<Days>;
  times: {
    startTime: string;
    endTime: string;
    duration: number;
    slotInterval: number;
  };
  tuitionInfo: {
    registrationFee: string;
    tuitionFee: string;
    costumeFee: string;
    maxSize: string;
    minWait: string;
    tuitionBillingMethod: string;
    tuitionBillingCycle: string;
    tuitionDiscountRule: string;
    hourlyTuitionBillingId: string;
    classCountTuitionBillingId: string;
    prorateTuition: boolean;
    prorateOneTimePackage: boolean;
    billingDay: number;
    billingFrequencyDays?: number;
  };
  otherInfo: {
    tags: Array<{
      label: string;
      value: string;
    }>;
    policyGroup: Array<{
      label: string;
      value: string;
    }>;
    group: Array<{
      label: string;
      value: string;
    }>;
    description: string;
  };
  timeConfig: 'same' | 'different';
  dayTimes: Record<
    string,
    {
      startTime: string;
      endTime: string;
    }
  >;
  displayOptions: {
    isPrivateLesson: boolean;
    displayOnWebsite: boolean;
    allowPortalEnrollment: boolean;
    allowOnlineRegistration: boolean;
    allowTrialEnrollment: boolean;
    includeInPublicSchedule: boolean;
    acceptsMakeups: boolean;
  };
}

export const UpdateClassPage: React.FC = () => {
  const { classId } = useParams({
    from: '/_auth/_layout/classes/$classId/update',
  });
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const navigate = useNavigate({ from: '/classes/$classId/update' });
  const mutation = useUpdateClassMutation();
  const { data: studioDetails } = useGetStudioDetails();
  const studioProrationEnabled = studioDetails?.prorationEnabled !== false;
  // const { promise: sessionPromise } = useGetAllSessionsOld();
  const { data, isFetching, promise } = useGetClass({ classId });
  const { data: hourlyData } = useGetHourlyTuitionBilling();
  const { data: classCountCharts } = useGetClassCountTuitionBilling(
    !!studioDetails?.familyClassCountTuitionEnabled,
  );
  const familyClassCountEnabled =
    !!studioDetails?.familyClassCountTuitionEnabled;
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    trigger,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    defaultValues: async () => {
      const classData = await promise;

      // Make sure all string fields have default values to avoid undefined
      return {
        classInfo: {
          className: classData.title || '',
          location: {
            label: classData.location?.fieldName ?? null,
            value: classData.location?._id ?? null,
          },
          room: {
            label: classData.room?.fieldName ?? null,
            value: classData.room?._id ?? null,
          },
          instructor: classData.instructor
            ? classData.instructor.map((inst) => ({
                label: inst.fieldName,
                value: inst._id,
              }))
            : [],
          session: classData.session
            ? {
                value: classData.session._id ?? '',
                label: classData.session.name ?? '',
              }
            : { value: '', label: '' },
          seats: classData.maxSize || 0,
          minAge: {
            year: classData.startYear || 0,
            month: classData.startMonth || 0,
          },
          maxAge: {
            year: classData.endYear || 0,
            month: classData.endMonth || 0,
          },
          hide: classData.hide || false,
          color: classData.color || '',
        },
        dates: {
          startDate: extractDateOnly(classData.startDate) || '',
          endDate: extractDateOnly(classData.endDate) || '',
          registrationStartDate: classData.registrationStartDate
            ? extractDateOnly(classData.registrationStartDate)
            : '',
        },
        days: classData.days ?? [],
        times: {
          startTime:
            !classData.startTime?.trim() && !classData.endTime?.trim()
              ? '00:00'
              : classData.startTime || '',
          endTime:
            !classData.startTime?.trim() && !classData.endTime?.trim()
              ? '00:00'
              : classData.endTime || '',
          duration: classData.isPrivateLesson
            ? classData.duration || 30
            : classData.duration ||
              (!classData.startTime?.trim() && !classData.endTime?.trim()
                ? 0
                : calculateDurationInMinutes(
                    classData.startTime || '',
                    classData.endTime || '',
                  )) ||
              0,
          slotInterval: classData.slotInterval || 30,
        },
        tuitionInfo: {
          registrationFee: classData.registrationFeeAmount
            ? classData.registrationFeeAmount.toString()
            : '',
          tuitionFee: classData.tuitionFee
            ? classData.tuitionFee.toString()
            : '',
          costumeFee: classData.costumeFee
            ? classData.costumeFee.toString()
            : '',
          maxSize: classData.maxSize ? classData.maxSize.toString() : '',
          minWait: '',
          tuitionBillingMethod: classData.tuitionBillingMethod || '',
          tuitionBillingCycle: classData.tuitionBillingCycle || '',
          hourlyTuitionBillingId:
            getHourlyTuitionBillingId(classData.hourlyTuitionBillingId) ?? '',
          classCountTuitionBillingId:
            getClassCountTuitionBillingId(
              classData.classCountTuitionBillingId,
            ) ?? '',
          tuitionDiscountRule: classData.tuitionDiscountRule || '',
          prorateTuition: classData.prorateTuition ?? true,
          prorateOneTimePackage: classData.prorateOneTimePackage === true,
          billingDay: classData.billingDay || 1,
          billingFrequencyDays: classData.billingFrequencyDays || undefined,
        },
        otherInfo: {
          tags:
            classData.tags?.map((tag) => ({
              label: tag.fieldName,
              value: tag._id,
            })) ?? [],
          policyGroup:
            classData.policyGroup?.map((policy) => ({
              label: policy.name,
              value: policy._id,
            })) ?? [],
          group:
            classData.group?.map((grp) => ({
              label: grp.fieldName,
              value: grp._id,
            })) ?? [],
          description: classData.description || '',
        },
        timeConfig: (classData.timeConfig as 'same' | 'different') || 'same',
        dayTimes: (classData.availability || []).reduce<
          Record<string, { startTime: string; endTime: string }>
        >((acc, { day, startTime, endTime }) => {
          const parsedStart =
            startTime === 'Invalid Date' || !startTime
              ? format(new Date(), 'HH:mm')
              : normalizeTime(startTime);
          const parsedEnd =
            endTime === 'Invalid Date' || !endTime
              ? format(new Date(), 'HH:mm')
              : normalizeTime(endTime);
          return {
            ...acc,
            [day]: {
              startTime: parsedStart,
              endTime: parsedEnd,
            },
          };
        }, {}),
        displayOptions: {
          isPrivateLesson: classData.isPrivateLesson ?? false,
          displayOnWebsite: classData.displayOnWebsite ?? false,
          allowPortalEnrollment: classData.allowPortalEnrollment ?? false,
          allowOnlineRegistration: classData.allowOnlineRegistration ?? false,
          allowTrialEnrollment: classData.allowTrialEnrollment ?? false,
          includeInPublicSchedule: classData.includeInPublicSchedule ?? false,
          acceptsMakeups: classData.acceptsMakeups !== false,
        },
      } as FormData;
    },
    mode: 'onChange',
  });

  const { dialog: unsavedChangesDialog, allowNextNavigation } =
    useUnsavedChangesBlocker(isDirty);

  useEffect(() => {
    // Register days field with validation
    register('days', {
      validate: (value) => value.length > 0 || 'Please select at least one day',
    });
  }, [register]);

  const startTimeValue = watch('times.startTime');
  const endTimeValue = watch('times.endTime');
  const startTime = typeof startTimeValue === 'string' ? startTimeValue : '';
  const endTime = typeof endTimeValue === 'string' ? endTimeValue : '';
  const tuitionBillingCycle = watch('tuitionInfo.tuitionBillingCycle');

  const isPrivateLesson = watch('displayOptions.isPrivateLesson');

  const [lastEditedAgeSide, setLastEditedAgeSide] = useState<'min' | 'max'>(
    'max',
  );
  const ageRangeErrorSide = hasAgeRangeError(
    errors.classInfo?.minAge,
    errors.classInfo?.maxAge,
  )
    ? lastEditedAgeSide
    : null;

  const minAgeYear = watch('classInfo.minAge.year');
  const minAgeMonth = watch('classInfo.minAge.month');
  const maxAgeYear = watch('classInfo.maxAge.year');
  const maxAgeMonth = watch('classInfo.maxAge.month');

  // Revalidate the whole range so a fix on one side clears the other side's error.
  useEffect(() => {
    if (!ageRangeErrorSide) return;
    void trigger([
      'classInfo.minAge.year',
      'classInfo.minAge.month',
      'classInfo.maxAge.year',
      'classInfo.maxAge.month',
    ]);
  }, [
    ageRangeErrorSide,
    minAgeYear,
    minAgeMonth,
    maxAgeYear,
    maxAgeMonth,
    trigger,
  ]);

  useEffect(() => {
    if (isPrivateLesson) {
      return;
    }
    const start = startTime.trim();
    const end = endTime.trim();
    if (start && end) {
      setValue('times.duration', calculateDurationInMinutes(start, end));
    } else {
      setValue('times.duration', 0);
    }
  }, [startTime, endTime, setValue, isPrivateLesson]);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const formDays = watch('days') ?? [];

  const [selectedFile, setSelectedFile] = useState<File | string | null>(null);
  const [selectedFileType, setSelectedFileType] = useState<'file' | 'url'>(
    'file',
  );
  const [isFileChanged, setIsFileChanged] = useState(false);

  const handleFileSelect = (
    file: File | string | null,
    fileType: 'file' | 'url',
  ) => {
    setSelectedFile(file);
    setSelectedFileType(fileType);
    setIsFileChanged(true);
  };

  // Watch for session changes
  const sessionId = watch('classInfo.session.value');

  // Initial values from backend (for conditional billing-date validation)
  const initialSessionId = data?.session?._id ?? '';
  const initialStartDate = data?.startDate
    ? extractDateOnly(data.startDate)
    : '';

  const isBillingDateUnchangedFromBackend = (
    currentSessionId: string,
    currentStartDate: string,
  ) =>
    initialSessionId !== '' &&
    initialStartDate !== '' &&
    currentSessionId === initialSessionId &&
    currentStartDate === initialStartDate;

  // Fetch session data
  const { data: sessionData } = useGetSessionById(sessionId, {
    enabled: !!sessionId,
  });

  const onSubmit = handleSubmit(async (data: FormData) => {
    const {
      dates,
      times,
      classInfo,
      otherInfo,
      tuitionInfo,
      days,
      timeConfig,
      displayOptions,
    } = data;

    // Validate that at least one day is selected
    if (days.length === 0) {
      toast.error('Please select at least one day');
      return;
    }

    // Convert YYYY-MM-DD back to the format expected by the backend
    const formatDateForBackend = (dateString: string) => {
      if (!dateString) return '';
      console.log('dateString', dateString);
      // Create a date at midnight UTC to match backend expectation
      return `${dateString}T00:00:00.000Z`;
    };

    const startDate = formatDateForBackend(dates.startDate);
    const endDate = formatDateForBackend(dates.endDate);
    const startTime = normalizeTime(times.startTime);
    const endTime = normalizeTime(times.endTime);

    const availability =
      timeConfig === 'same'
        ? days.map((day) => ({
            day,
            startTime: formatDateTime(startDate, startTime),
            endTime: formatDateTime(startDate, endTime),
          }))
        : days.map((day) => {
            const dayST = normalizeTime(
              data.dayTimes[day]?.startTime ?? startTime,
            );
            const dayET = normalizeTime(data.dayTimes[day]?.endTime ?? endTime);
            return {
              day,
              startTime: formatDateTime(startDate, dayST),
              endTime: formatDateTime(startDate, dayET),
            };
          });

    try {
      await mutation.mutateAsync({
        classId,
        file: isFileChanged ? selectedFile : null,
        fileType: selectedFileType,
        classDetails: {
          title: classInfo.className,
          color: classInfo.color,
          hide: classInfo.hide,
          location: classInfo.location.value,
          room: classInfo.room.value,
          instructor: classInfo.instructor.map(
            (instructor) => instructor.value,
          ),
          maxSize: Number(classInfo.seats),
          startYear: classInfo.minAge.year,
          startMonth: classInfo.minAge.month,
          endYear: classInfo.maxAge.year,
          endMonth: classInfo.maxAge.month,
          group: otherInfo.group.map((group) => group.value),
          tags: otherInfo.tags.map((tag) => tag.value),
          startDate: startDate,
          endDate: endDate,
          startTime: startTime,
          endTime: endTime,
          duration: times.duration,
          isPrivateLesson: displayOptions.isPrivateLesson,
          slotInterval: displayOptions.isPrivateLesson
            ? Number(times.slotInterval)
            : undefined,
          days: days,
          registrationStartDate: dates.registrationStartDate
            ? formatDateForBackend(dates.registrationStartDate)
            : '',
          registrationFeeAmount:
            data.tuitionInfo.tuitionBillingCycle === 'hourly'
              ? 0
              : Number(data.tuitionInfo.registrationFee),
          tuitionFee:
            data.tuitionInfo.tuitionBillingCycle === 'hourly'
              ? 0
              : Number(data.tuitionInfo.tuitionFee),
          tuitionBillingCycle: tuitionInfo.tuitionBillingCycle,
          hourlyTuitionBillingId:
            data.tuitionInfo.tuitionBillingCycle === 'hourly'
              ? data.tuitionInfo.hourlyTuitionBillingId
              : null,
          classCountTuitionBillingId:
            data.tuitionInfo.tuitionBillingCycle === 'monthly' &&
            data.tuitionInfo.classCountTuitionBillingId
              ? data.tuitionInfo.classCountTuitionBillingId
              : null,
          prorateTuition: tuitionInfo.prorateTuition,
          prorateOneTimePackage:
            tuitionInfo.tuitionBillingCycle === TuitionBillingCycle.ONE_TIME
              ? tuitionInfo.prorateOneTimePackage
              : false,
          description: otherInfo.description,
          policyGroup: otherInfo.policyGroup.map((policy) => policy.value),
          availability,
          timeConfig: timeConfig,
          session: classInfo.session.value,
          costumeFee:
            data.tuitionInfo.tuitionBillingCycle === 'hourly'
              ? 0
              : Number(data.tuitionInfo.costumeFee),
          ...(data.tuitionInfo.tuitionBillingCycle === 'monthly' && {
            billingDay: data.tuitionInfo.billingDay,
          }),
          ...(data.tuitionInfo.tuitionBillingCycle ===
            TuitionBillingCycle.EVERY_X_DAYS && {
            billingFrequencyDays: data.tuitionInfo.billingFrequencyDays,
          }),
          displayOnWebsite: displayOptions.isPrivateLesson
            ? false
            : displayOptions.displayOnWebsite,
          allowPortalEnrollment: displayOptions.isPrivateLesson
            ? false
            : displayOptions.allowPortalEnrollment,
          allowOnlineRegistration: displayOptions.allowOnlineRegistration,
          allowTrialEnrollment: displayOptions.isPrivateLesson
            ? false
            : displayOptions.allowTrialEnrollment,
          includeInPublicSchedule: displayOptions.isPrivateLesson
            ? false
            : displayOptions.includeInPublicSchedule,
          acceptsMakeups: displayOptions.acceptsMakeups,
        },
      });
      // Clear dirty state + bypass blocker before navigate (isDirty lags one render).
      reset(data);
      allowNextNavigation();
      toast.success('Class updated successfully!');
      void navigate({
        to: displayOptions.isPrivateLesson
          ? '/classes/private-lessons'
          : '/classes',
      });
    } catch (error) {
      console.error('Failed to update class:', error);
      const { message: apiMessage, code } = getClassUpdateApiError(error);

      // Class fields were already persisted; treat as partial success.
      if (code) {
        invalidateAfterClassUpdate(classId);
        reset(data);
        allowNextNavigation();
        toast.warning(
          apiMessage ??
            'Your class changes were saved, but Enrollio CRM calendar sync needs attention.',
        );
        void navigate({
          to: displayOptions.isPrivateLesson
            ? '/classes/private-lessons'
            : '/classes',
        });
        return;
      }

      toast.error(apiMessage ?? 'Failed to update class. Please try again.');
    }
  });

  if (isFetching) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <Loader2 className='size-8 animate-spin' />
      </div>
    );
  }

  if (!data) {
    return <div>Class not found</div>;
  }

  return (
    <div className='pb-56'>
      {canGoBack ? (
        <Button
          variant={'ghost'}
          className='flex items-center p-2 [&_svg]:size-6'
          disabled={!canGoBack}
          onClick={() => {
            router.history.back();
          }}
        >
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>Edit Class</h2>
        </Button>
      ) : (
        <Link
          to={data.isPrivateLesson ? '/classes/private-lessons' : '/classes'}
          className='flex items-center gap-2'
        >
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>Edit Class</h2>
        </Link>
      )}

      <Card className='mx-auto mt-6 w-full max-w-4xl border border-border shadow-none'>
        <CoverImageSelector
          onFileSelect={handleFileSelect}
          imageUrl={data.imageUrl}
        />
        <CardContent>
          <form onSubmit={onSubmit} className='space-y-6'>
            {/* Class Info Section */}
            <div className='space-y-4'>
              <h2 className='text-xl font-semibold'>Class Info</h2>
              <div className='space-y-2'>
                <Label>Class Name</Label>
                <Input
                  {...register('classInfo.className', {
                    required: 'Class name is required',
                  })}
                  placeholder='Class Name'
                />
                {typeof errors.classInfo?.className?.message === 'string' && (
                  <span className='text-sm text-destructive'>
                    {errors.classInfo.className.message}
                  </span>
                )}
              </div>
              <ClassColorPicker
                value={watch('classInfo.color')}
                onChange={(next) => {
                  setValue('classInfo.color', next, { shouldDirty: true });
                }}
                selectTriggerId='class-info-color-select'
                inputId='class-info-color-hex'
                resetPreferCustomKey={classId}
              />
              <div className='space-y-2'>
                <div className='flex items-center space-x-2'>
                  <Switch
                    id='hidden'
                    checked={watch('classInfo.hide')}
                    onCheckedChange={(checked) => {
                      setValue('classInfo.hide', checked);
                    }}
                  />
                  <Label htmlFor='hidden'>
                    Hide from parent portal - not shown in family browse or
                    search
                  </Label>
                </div>
              </div>

              <div className='flex items-start gap-2'>
                <Switch
                  id='upd-includeInPublicSchedule'
                  className='mt-0.5 shrink-0'
                  checked={watch('displayOptions.includeInPublicSchedule')}
                  onCheckedChange={(v) => {
                    setValue('displayOptions.includeInPublicSchedule', v);
                  }}
                />
                <Label
                  htmlFor='upd-includeInPublicSchedule'
                  className='min-w-0 cursor-pointer font-normal'
                >
                  Include this class to the &nbsp;
                  <Link
                    to='/public-page-link'
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                    }}
                    className='font-medium text-primary underline underline-offset-4 hover:no-underline'
                  >
                    Public Class Directory
                  </Link>
                </Label>
              </div>
              <ClassPublicShareLinkSection
                classId={classId}
                classData={data}
                currentStudio={
                  studioDetails
                    ? {
                        _id: studioDetails._id,
                        locationId: studioDetails.locationId,
                      }
                    : undefined
                }
              />

              <div className='flex items-start gap-2'>
                <Switch
                  id='upd-acceptsMakeups'
                  className='mt-0.5 shrink-0'
                  checked={watch('displayOptions.acceptsMakeups')}
                  onCheckedChange={(v) => {
                    setValue('displayOptions.acceptsMakeups', v);
                  }}
                />
                <div className='min-w-0'>
                  <Label
                    htmlFor='upd-acceptsMakeups'
                    className='cursor-pointer font-normal'
                  >
                    Accept makeup bookings
                  </Label>
                  <p className='text-xs text-muted-foreground'>
                    Allow Admins and Parents to book Makeup Classes
                  </p>
                </div>
              </div>

              <div className='space-y-2'>
                <Label>Location</Label>
                <Controller
                  control={control}
                  name='classInfo.location'
                  render={({ field }) => (
                    <LocationSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {typeof errors.classInfo?.location?.message === 'string' && (
                  <span className='text-sm text-destructive'>
                    {errors.classInfo.location.message}
                  </span>
                )}
              </div>
              <div className='space-y-2'>
                <Label>Room</Label>
                <Controller
                  control={control}
                  name='classInfo.room'
                  rules={{
                    validate: (value) =>
                      value.value !== '' || 'Room selection is required',
                  }}
                  render={({ field }) => (
                    <RoomSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {typeof errors.classInfo?.room?.message === 'string' && (
                  <span className='text-sm text-destructive'>
                    {errors.classInfo.room.message}
                  </span>
                )}
              </div>
              <div className='space-y-2'>
                <Label>Capacity</Label>
                <Input
                  {...register('classInfo.seats', {
                    required: 'Capacity is required',
                    min: {
                      value: 1,
                      message: 'Capacity must be at least 1',
                    },
                    max: isPrivateLesson
                      ? {
                          value: 3,
                          message:
                            'Private lesson capacity must be between 1 and 3',
                        }
                      : undefined,
                    valueAsNumber: true,
                  })}
                  placeholder={
                    isPrivateLesson ? 'Seats per slot (1–3)' : 'Capacity'
                  }
                  type='number'
                  min={1}
                  max={isPrivateLesson ? 3 : undefined}
                />
                {typeof errors.classInfo?.seats?.message === 'string' && (
                  <span className='text-sm text-destructive'>
                    {errors.classInfo.seats.message}
                  </span>
                )}
                {isPrivateLesson && (
                  <p className='text-xs text-muted-foreground'>
                    Use 1 for private, or 2–3 for semi-private lessons.
                  </p>
                )}
              </div>
              <div className='space-y-2'>
                <div className='flex gap-4'>
                  <div className='flex-1 space-y-2'>
                    <Label>Min Age</Label>
                    <div
                      className='flex gap-2'
                      onInput={() => setLastEditedAgeSide('min')}
                    >
                      <Input
                        type='number'
                        {...register('classInfo.minAge.year', {
                          required: 'Minimum age year is required',
                          min: {
                            value: 0,
                            message: 'Minimum age year must be at least 1',
                          },
                          max: {
                            value: MAX_AGE_YEARS,
                            message: `Minimum age year must be at most ${MAX_AGE_YEARS}.`,
                          },
                          validate: (_value, formValues) =>
                            validateAgeRange(
                              formValues.classInfo.minAge,
                              formValues.classInfo.maxAge,
                            ),
                          valueAsNumber: true,
                        })}
                        placeholder='Year'
                      />
                      <Input
                        type='number'
                        {...register('classInfo.minAge.month', {
                          required: 'Minimum age month is required',
                          min: {
                            value: 0,
                            message: 'Minimum age month must be at least 0',
                          },
                          max: {
                            value: 11,
                            message: 'Minimum age month must be at most 11',
                          },
                          validate: (_value, formValues) =>
                            validateAgeRange(
                              formValues.classInfo.minAge,
                              formValues.classInfo.maxAge,
                            ),
                          valueAsNumber: true,
                        })}
                        placeholder='Month'
                      />
                    </div>
                    {fieldErrorMessage(
                      errors.classInfo?.minAge?.year?.message,
                    ) && (
                      <span className='text-sm text-destructive'>
                        {fieldErrorMessage(
                          errors.classInfo?.minAge?.year?.message,
                        )}
                      </span>
                    )}
                    {fieldErrorMessage(
                      errors.classInfo?.minAge?.month?.message,
                    ) && (
                      <span className='text-sm text-destructive'>
                        {fieldErrorMessage(
                          errors.classInfo?.minAge?.month?.message,
                        )}
                      </span>
                    )}
                    {ageRangeErrorSide === 'min' && (
                      <span className='text-sm text-destructive'>
                        {AGE_RANGE_REVERSED_MESSAGE}
                      </span>
                    )}
                  </div>
                  <div className='flex-1 space-y-2'>
                    <Label>Max Age</Label>
                    <div
                      className='flex gap-2'
                      onInput={() => setLastEditedAgeSide('max')}
                    >
                      <Input
                        type='number'
                        {...register('classInfo.maxAge.year', {
                          required: 'Maximum age year is required',
                          min: {
                            value: 1,
                            message: 'Maximum age year must be at least 1',
                          },
                          max: {
                            value: MAX_AGE_YEARS,
                            message: `Maximum age year must be at most ${MAX_AGE_YEARS}.`,
                          },
                          validate: (_value, formValues) =>
                            validateAgeRange(
                              formValues.classInfo.minAge,
                              formValues.classInfo.maxAge,
                            ),
                          valueAsNumber: true,
                        })}
                        placeholder='Year'
                      />
                      <Input
                        type='number'
                        {...register('classInfo.maxAge.month', {
                          required: 'Maximum age month is required',
                          min: {
                            value: 0,
                            message: 'Maximum age month must be at least 0',
                          },
                          max: {
                            value: 11,
                            message: 'Maximum age month must be at most 11',
                          },
                          validate: (_value, formValues) =>
                            validateAgeRange(
                              formValues.classInfo.minAge,
                              formValues.classInfo.maxAge,
                            ),
                          valueAsNumber: true,
                        })}
                        placeholder='Month'
                      />
                    </div>
                    {fieldErrorMessage(
                      errors.classInfo?.maxAge?.year?.message,
                    ) && (
                      <span className='text-sm text-destructive'>
                        {fieldErrorMessage(
                          errors.classInfo?.maxAge?.year?.message,
                        )}
                      </span>
                    )}
                    {fieldErrorMessage(
                      errors.classInfo?.maxAge?.month?.message,
                    ) && (
                      <span className='text-sm text-destructive'>
                        {fieldErrorMessage(
                          errors.classInfo?.maxAge?.month?.message,
                        )}
                      </span>
                    )}
                    {ageRangeErrorSide === 'max' && (
                      <span className='text-sm text-destructive'>
                        {AGE_RANGE_REVERSED_MESSAGE}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className='space-y-2'>
                <Label>Instructor</Label>
                <Controller
                  control={control}
                  name='classInfo.instructor'
                  render={({ field }) => (
                    <InstructorSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className='space-y-2'>
                <Label>Description</Label>
                <textarea
                  {...register('otherInfo.description')}
                  placeholder='Enter Class description'
                  className='h-24 w-full rounded-md border p-2'
                />
              </div>
            </div>
            <div className='space-y-2'>
              <Label>Session</Label>
              <Controller
                control={control}
                name='classInfo.session'
                rules={{
                  validate: (session, formValues) => {
                    if ((session && !session.value) || session === undefined)
                      return true;

                    if (!formValues.dates.startDate) return true;

                    if (!sessionData) return true;

                    if (!('billingDate' in sessionData)) return true;

                    if (!sessionData.billingDate) return true;

                    if (
                      isBillingDateUnchangedFromBackend(
                        formValues.classInfo?.session?.value,
                        formValues.dates.startDate,
                      )
                    ) {
                      return true;
                    }

                    const billingMoment = moment(
                      sessionData.billingDate,
                    ).startOf('day');
                    const classMoment = moment(
                      formValues.dates.startDate,
                    ).startOf('day');

                    const formattedBillingDate =
                      billingMoment.format('MMM D, YYYY');

                    return (
                      billingMoment.isSameOrBefore(classMoment) ||
                      `Session billing date (${formattedBillingDate}) cannot be after class start date`
                    );
                  },
                }}
                render={({ field }) => {
                  return (
                    <SessionSelector
                      session={field.value}
                      onChange={field.onChange}
                    />
                  );
                }}
              />
              {typeof errors.classInfo?.session?.message === 'string' && (
                <span className='text-sm text-destructive'>
                  {errors.classInfo.session.message}
                </span>
              )}
            </div>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <div className='space-y-2'>
                <Label>Start Date</Label>
                <Controller
                  control={control}
                  name='dates.startDate'
                  rules={{
                    required: 'Start date is required',
                    validate: (value, formValues) => {
                      if (!value) return true;

                      // Check if we have a session selected
                      const sessionValue =
                        formValues.classInfo?.session?.value;
                      if (!sessionValue) return true;

                      // Check if session data is available
                      if (!sessionData) return true;

                      // Type guard to ensure sessionData is a valid session object
                      if (!('billingDate' in sessionData)) return true;

                      // Check if billing date exists
                      if (!sessionData.billingDate) return true;

                      // Compare dates using moment
                      const billingMoment = moment(
                        sessionData.billingDate,
                      ).startOf('day');
                      const classMoment = moment(value).startOf('day');

                      const formattedBillingDate =
                        billingMoment.format('MMM D, YYYY');

                      if (
                        formValues.tuitionInfo.tuitionBillingCycle ===
                          'weekly' &&
                        calculateDateDifference(
                          value,
                          formValues.dates.endDate,
                        ) < 7
                      ) {
                        return 'Class must be at least 7 days long';
                      }

                      if (
                        formValues.tuitionInfo.tuitionBillingCycle ===
                          'bi-weekly' &&
                        calculateDateDifference(
                          value,
                          formValues.dates.endDate,
                        ) < 14
                      ) {
                        return 'Class must be at least 14 days long';
                      }

                      if (
                        formValues.tuitionInfo.tuitionBillingCycle ===
                          'monthly' &&
                        calculateDateDifference(
                          value,
                          formValues.dates.endDate,
                        ) < 30
                      ) {
                        return 'Class must be at least 30 days long';
                      }

                      if (
                        isBillingDateUnchangedFromBackend(
                          formValues.classInfo?.session?.value,
                          value,
                        )
                      ) {
                        return true;
                      }

                      return (
                        billingMoment.isSameOrBefore(classMoment) ||
                        `Class start date must be on or after session billing date (${formattedBillingDate})`
                      );
                    },
                  }}
                  render={({ field }) => (
                    <DateTimePicker
                      hourCycle={24}
                      granularity='day'
                      displayFormat={{ hour24: 'PPP' }}
                      value={
                        field.value
                          ? new Date(`${field.value}T00:00:00`) // Create date without timezone
                          : undefined
                      }
                      onChange={(date) => {
                        if (!date) {
                          field.onChange(undefined);
                          return;
                        }
                        // Just keep the date part
                        field.onChange(
                          `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`,
                        );
                      }}
                    />
                  )}
                />
                {errors.dates?.startDate && (
                  <span className='text-sm text-destructive'>
                    {errors.dates.startDate.message}
                  </span>
                )}
              </div>
              <div className='space-y-2'>
                <Label>End Date</Label>
                <Controller
                  control={control}
                  name='dates.endDate'
                  rules={{
                    required: 'End date is required',
                    validate: (value, formValues) => {
                      if (!value || !formValues.dates.startDate) return true;
                      return (
                        new Date(
                          value.includes('T') ? value : value + 'T00:00:00',
                        ) >
                          new Date(
                            formValues.dates.startDate.includes('T')
                              ? formValues.dates.startDate
                              : formValues.dates.startDate + 'T00:00:00',
                          ) || 'End date must be after start date'
                      );
                    },
                  }}
                  render={({ field }) => {
                    return (
                      <DateTimePicker
                        hourCycle={24}
                        granularity='day'
                        displayFormat={{ hour24: 'PPP' }}
                        value={
                          field.value
                            ? new Date(`${field.value}T00:00:00`) // Create date without timezone
                            : undefined
                        }
                        onChange={(date) => {
                          if (!date) {
                            field.onChange(undefined);
                            return;
                          }
                          // Just keep the date part
                          field.onChange(
                            `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`,
                          );
                        }}
                      />
                    );
                  }}
                />
                {errors.dates?.endDate && (
                  <span className='text-sm text-destructive'>
                    {errors.dates.endDate.message}
                  </span>
                )}
              </div>
            </div>

            <div className='space-y-2'>
              <Label>Registration Start Date</Label>
              <Controller
                control={control}
                name='dates.registrationStartDate'
                rules={{
                  validate: (value, formValues) => {
                    if (!value || !formValues.dates.endDate) return true;
                    return (
                      new Date(
                        value.includes('T') ? value : value + 'T00:00:00',
                      ) <=
                        new Date(
                          formValues.dates.endDate.includes('T')
                            ? formValues.dates.endDate
                            : formValues.dates.endDate + 'T00:00:00',
                        ) ||
                      'Registration start date must be before or on the start date'
                    );
                  },
                }}
                render={({ field }) => (
                  <DateTimePicker
                    hourCycle={24}
                    granularity='day'
                    displayFormat={{ hour24: 'PPP' }}
                    value={
                      field.value
                        ? new Date(`${field.value}T00:00:00`) // Create date without timezone
                        : undefined
                    }
                    onChange={(date) => {
                      if (!date) {
                        field.onChange(undefined);
                        return;
                      }
                      // Just keep the date part
                      field.onChange(
                        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`,
                      );
                    }}
                  />
                )}
              />
              {errors.dates?.registrationStartDate && (
                <span className='text-sm text-destructive'>
                  {errors.dates.registrationStartDate.message}
                </span>
              )}
            </div>

            {/* Times Section */}
            <div className='space-y-4'>
              <h2 className='text-xl font-semibold'>Times</h2>

              {/* Time Configuration Dropdown */}
              <div className='space-y-2'>
                <Label>Time Configuration</Label>
                <Select
                  value={watch('timeConfig')}
                  onValueChange={(value) => {
                    setValue('timeConfig', value as 'same' | 'different');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select time configuration' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='same'>
                      Same Time for All Selected Days
                    </SelectItem>
                    <SelectItem value='different'>
                      Different Times for Each Selected Day
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {watch('timeConfig') === 'same' ? (
                <div className='flex flex-col gap-4 md:flex-row md:gap-4'>
                  <div className='min-w-0 flex-1 space-y-2'>
                    <Label>
                      {isPrivateLesson
                        ? 'Availability start time'
                        : 'Start Time'}
                    </Label>
                    <Controller
                      control={control}
                      name='times.startTime'
                      rules={{ required: 'Start time is required' }}
                      render={({ field }) => (
                        <TimePickerDropdown
                          value={field.value}
                          onChange={(date) => {
                            field.onChange(date);
                          }}
                        />
                      )}
                    />
                    {errors.times?.startTime && (
                      <span className='text-sm text-destructive'>
                        {errors.times.startTime.message}
                      </span>
                    )}
                  </div>
                  <div className='min-w-0 flex-1 space-y-2'>
                    <Label>
                      {isPrivateLesson ? 'Availability end time' : 'End Time'}
                    </Label>
                    <Controller
                      control={control}
                      name='times.endTime'
                      rules={{
                        required: 'End time is required',
                        validate: (value) => {
                          const raw = watch('times.startTime');
                          const currentStartTime =
                            typeof raw === 'string' ? raw : '';
                          if (!currentStartTime || !value) return true;
                          return (
                            isValidTimeRange(currentStartTime, value) ||
                            'End time must be after start time'
                          );
                        },
                      }}
                      render={({ field }) => (
                        <TimePickerDropdown
                          value={field.value}
                          onChange={(date) => {
                            field.onChange(date);
                          }}
                        />
                      )}
                    />
                    {errors.times?.endTime && (
                      <span className='text-sm text-destructive'>
                        {errors.times.endTime.message}
                      </span>
                    )}
                  </div>
                  {!isPrivateLesson && (
                    <div className='min-w-0 flex-1 space-y-2'>
                      <Label>Duration</Label>
                      <Input
                        type='text'
                        {...register('times.duration')}
                        readOnly
                        value={
                          !startTime.trim() || !endTime.trim()
                            ? '00:00'
                            : calculateDurationInMinutes(startTime, endTime)
                        }
                        placeholder='MM'
                      />
                    </div>
                  )}
                </div>
              ) : (
                // New UI for different times per day
                <div className='space-y-6'>
                  {/* Column Headers */}
                  <div className='hidden items-center gap-4 md:flex'>
                    <div className='w-20'></div>
                    <div className='flex flex-1 space-x-4'>
                      <div className='flex-1 text-center'>
                        <Label>Start Time</Label>
                      </div>
                      <div className='flex w-8 items-center'></div>
                      <div className='flex-1 text-center'>
                        <Label>End Time</Label>
                      </div>
                    </div>
                  </div>

                  {/* Day rows */}
                  {DAYS.map((day, index) => (
                    <div key={day}>
                      <div className='flex flex-col gap-3 md:h-auto md:flex-row md:items-center md:gap-4'>
                        <div className='flex items-center gap-4'>
                        <Checkbox
                          id={day}
                          checked={formDays.includes(day)}
                          onCheckedChange={(checked) => {
                            const currentDays = formDays;
                            if (checked) {
                              setValue('days', [...currentDays, day], {
                                shouldValidate: true,
                              });
                              setValue(`dayTimes.${day}`, {
                                startTime: format(new Date(), 'HH:mm'),
                                endTime: format(new Date(), 'HH:mm'),
                              });
                            } else {
                              setValue(
                                'days',
                                currentDays.filter((d) => d !== day),
                                { shouldValidate: true },
                              );
                              setValue(`dayTimes.${day}`, {
                                startTime: '',
                                endTime: '',
                              });
                            }
                          }}
                        />
                        <Label htmlFor={day} className='w-20'>
                          {day}
                        </Label>
                        </div>

                        <div className='flex min-w-0 flex-1 flex-col gap-3 md:flex-row md:space-x-4'>
                          {formDays.includes(day) ? (
                            <>
                              <div className='min-w-0 flex-1'>
                                <p className='mb-1 text-sm font-medium md:hidden'>
                                  Start Time
                                </p>
                                <Controller
                                  control={control}
                                  name={`dayTimes.${day}.startTime`}
                                  rules={{ required: 'Start time is required' }}
                                  render={({ field }) => {
                                    const value = field.value
                                      ? format(
                                          parse(
                                            field.value,
                                            field.value.length <= 5
                                              ? 'HH:mm'
                                              : 'MMM d, yyyy, h:mm:ss a',
                                            new Date(),
                                          ),
                                          'HH:mm',
                                        )
                                      : format(new Date(), 'HH:mm');
                                    return (
                                      <TimePickerDropdown
                                        value={value}
                                        onChange={(time) => {
                                          field.onChange(time);
                                        }}
                                      />
                                    );
                                  }}
                                />
                              </div>
                              <div className='hidden w-8 items-center justify-center md:flex'></div>
                              <div className='min-w-0 flex-1'>
                                <p className='mb-1 text-sm font-medium md:hidden'>
                                  End Time
                                </p>
                                <Controller
                                  control={control}
                                  name={`dayTimes.${day}.endTime`}
                                  rules={{
                                    required: 'End time is required',
                                    validate: (value) => {
                                      const dayStartTime = watch(
                                        `dayTimes.${day}.startTime`,
                                      );
                                      if (!dayStartTime || !value) return true;
                                      return (
                                        isValidTimeRange(dayStartTime, value) ||
                                        'End time must be after start time'
                                      );
                                    },
                                  }}
                                  render={({ field }) => {
                                    const value = field.value
                                      ? format(
                                          parse(
                                            field.value,
                                            field.value.length <= 5
                                              ? 'HH:mm'
                                              : 'MMM d, yyyy, h:mm:ss a',
                                            new Date(),
                                          ),
                                          'HH:mm',
                                        )
                                      : format(new Date(), 'HH:mm');
                                    return (
                                      <TimePickerDropdown
                                        value={value}
                                        onChange={(time) => {
                                          field.onChange(time);
                                        }}
                                      />
                                    );
                                  }}
                                />
                              </div>
                            </>
                          ) : (
                            <div className='flex-1 italic text-gray-500'></div>
                          )}
                        </div>
                      </div>
                      {index < DAYS.length - 1 && (
                        <div className='my-4 h-px w-full bg-border' />
                      )}
                    </div>
                  ))}

                  {/* Add error message for days validation */}
                  {errors.days && (
                    <span className='text-sm text-destructive'>
                      {errors.days.message}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Days Section - Only show when using Same Time for All Selected Days */}
            {watch('timeConfig') === 'same' && (
              <div className='space-y-4'>
                <h2 className='text-xl font-semibold'>Days</h2>
                <div className='flex flex-wrap gap-4'>
                  {DAYS.map((day) => (
                    <div key={day} className='flex items-center space-x-2'>
                      <Checkbox
                        id={day}
                        checked={watch('days').includes(day)}
                        onCheckedChange={(checked) => {
                          const currentDays = watch('days');
                          const updatedDays = checked
                            ? [...currentDays, day]
                            : currentDays.filter((d) => d !== day);

                          // Set the value and force validation to run
                          setValue('days', updatedDays, {
                            shouldValidate: true,
                            shouldDirty: true,
                            shouldTouch: true,
                          });

                          // Force re-render to show validation message immediately
                          void trigger('days');
                        }}
                      />
                      <Label htmlFor={day}>{day}</Label>
                    </div>
                  ))}
                </div>
                {errors.days && (
                  <span className='mt-2 block text-sm font-medium text-destructive'>
                    {errors.days.message}
                  </span>
                )}
              </div>
            )}

            <PrivateLessonOfferingSection
              isPrivateLesson={isPrivateLesson}
              toggleId='upd-isPrivateLesson'
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
            />

            {/* Tuition Info Section */}
            <div className='space-y-4'>
              <h2 className='text-xl font-semibold'>Tuition Info</h2>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                {tuitionBillingCycle !== 'hourly' && (
                  <>
                    <div className='space-y-2'>
                      <Label>Membership Fee</Label>
                      <Input
                        {...register('tuitionInfo.registrationFee', {
                          min: {
                            value: 0,
                            message: 'Registration fee cannot be negative',
                          },
                          valueAsNumber: true,
                        })}
                        type='number'
                        min={0}
                        step='1'
                        placeholder='0.00'
                      />
                      {errors.tuitionInfo?.registrationFee && (
                        <span className='text-sm text-destructive'>
                          {errors.tuitionInfo.registrationFee.message}
                        </span>
                      )}
                    </div>

                    <div className='space-y-2'>
                      <Label>Costume Fee</Label>
                      <Input
                        {...register('tuitionInfo.costumeFee', {
                          min: { value: 0, message: 'Fee cannot be negative' },
                          pattern: {
                            value: /^\d+(\.\d{1,2})?$/,
                            message: 'Please enter a valid amount',
                          },
                        })}
                        type='number'
                        min={0}
                        step='1'
                        placeholder='0.00'
                      />
                      {errors.tuitionInfo?.costumeFee && (
                        <span className='text-sm text-destructive'>
                          {errors.tuitionInfo.costumeFee.message}
                        </span>
                      )}
                    </div>

                    <div className='space-y-2'>
                      <Label>Tuition Fee</Label>
                      <Input
                        {...register('tuitionInfo.tuitionFee', {
                          min: {
                            value: 0,
                            message: 'Tuition fee cannot be negative',
                          },
                          valueAsNumber: true,
                        })}
                        type='number'
                        min={0}
                        step='0.01'
                        placeholder='0.00'
                      />
                      {errors.tuitionInfo?.tuitionFee && (
                        <span className='text-sm text-destructive'>
                          {errors.tuitionInfo.tuitionFee.message}
                        </span>
                      )}
                    </div>
                  </>
                )}

                <div className='space-y-2'>
                  <Label>Tuition Billing Cycle</Label>
                  <Controller
                    control={control}
                    name='tuitionInfo.tuitionBillingCycle'
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select Billing Cycle' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='monthly'>Monthly</SelectItem>
                          <SelectItem value='one-time'>One Time</SelectItem>
                          <SelectItem value='weekly'>Weekly</SelectItem>
                          <SelectItem value='bi-weekly'>Bi Weekly</SelectItem>
                          <SelectItem value='hourly'>Hourly</SelectItem>
                          <SelectItem value={TuitionBillingCycle.EVERY_X_DAYS}>
                            Every X Days
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                {tuitionBillingCycle === TuitionBillingCycle.EVERY_X_DAYS && (
                  <div className='space-y-2'>
                    <Label>Billing Frequency (Days)</Label>
                    <Input
                      {...register('tuitionInfo.billingFrequencyDays', {
                        required:
                          'Billing frequency is required for every X days cycle',
                        min: {
                          value: 1,
                          message: 'Billing frequency must be at least 1 day',
                        },
                        valueAsNumber: true,
                      })}
                      type='number'
                      min={1}
                      placeholder='Enter number of days (e.g. 10)'
                      disabled
                    />
                    {errors.tuitionInfo?.billingFrequencyDays && (
                      <span className='text-sm text-destructive'>
                        {errors.tuitionInfo.billingFrequencyDays.message}
                      </span>
                    )}
                  </div>
                )}

                {tuitionBillingCycle === 'hourly' && (
                  <div className='space-y-2'>
                    <Label>Hourly Tuition</Label>
                    <Controller
                      control={control}
                      name='tuitionInfo.hourlyTuitionBillingId'
                      rules={{
                        required: 'Hourly tuition selection is required',
                      }}
                      render={({ field }) => (
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder='Select Hourly Tuition' />
                          </SelectTrigger>
                          <SelectContent>
                            {hourlyData?.map((item) => (
                              <SelectItem key={item._id} value={item._id}>
                                {item.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.tuitionInfo?.hourlyTuitionBillingId && (
                      <span className='text-sm text-destructive'>
                        {errors.tuitionInfo.hourlyTuitionBillingId.message}
                      </span>
                    )}
                  </div>
                )}
                {tuitionBillingCycle === 'monthly' &&
                  familyClassCountEnabled && (
                    <div className='space-y-2'>
                      <Label>Family class-count chart</Label>
                      <Controller
                        control={control}
                        name='tuitionInfo.classCountTuitionBillingId'
                        render={({ field }) => (
                          <Select
                            value={field.value || '__studio_default__'}
                            onValueChange={(value) => {
                              field.onChange(
                                value === '__studio_default__' ? '' : value,
                              );
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder='Studio default' />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value='__studio_default__'>
                                Studio default
                              </SelectItem>
                              {classCountCharts?.map((item) => (
                                <SelectItem key={item._id} value={item._id}>
                                  {item.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <p className='text-xs text-muted-foreground'>
                        Optional override. Leave on studio default to use the
                        chart from Family Class Tuition settings.
                      </p>
                    </div>
                  )}
                {tuitionBillingCycle === 'monthly' ? (
                  <div className='space-y-2'>
                    <Label>Billing Day</Label>
                    <Input
                      {...register('tuitionInfo.billingDay', {
                        required: 'Billing day is required',
                        min: {
                          value: 1,
                          message: 'Billing day must be at least 1',
                        },
                        max: {
                          value: 28,
                          message: 'Billing day must be at most 28',
                        },
                        valueAsNumber: true,
                      })}
                      type='number'
                      min={1}
                      max={28}
                      placeholder='Enter billing day (1-28)'
                    />
                    {errors.tuitionInfo?.billingDay && (
                      <span className='text-sm text-destructive'>
                        {errors.tuitionInfo.billingDay.message}
                      </span>
                    )}
                  </div>
                ) : null}

                {tuitionBillingCycle &&
                  tuitionBillingCycle !== 'hourly' &&
                  tuitionBillingCycle !== TuitionBillingCycle.ONE_TIME && (
                    <div className='col-span-2 flex items-center gap-2'>
                      <Switch
                        id='prorateTuition'
                        checked={
                          studioProrationEnabled &&
                          watch('tuitionInfo.prorateTuition')
                        }
                        onCheckedChange={(checked) => {
                          setValue('tuitionInfo.prorateTuition', checked);
                        }}
                        disabled={!studioProrationEnabled}
                      />
                      <Label
                        htmlFor='prorateTuition'
                        className={
                          !studioProrationEnabled ? 'text-muted-foreground' : ''
                        }
                      >
                        Enable Proration
                      </Label>
                      {!studioProrationEnabled && (
                        <span className='text-xs text-muted-foreground'>
                          (Proration is disabled at studio level)
                        </span>
                      )}
                    </div>
                  )}

                {tuitionBillingCycle === TuitionBillingCycle.ONE_TIME ? (
                  <div className='col-span-2 flex items-center gap-2'>
                    <Switch
                      id='prorateOneTimePackage'
                      checked={
                        studioProrationEnabled &&
                        watch('tuitionInfo.prorateOneTimePackage')
                      }
                      onCheckedChange={(checked) => {
                        setValue('tuitionInfo.prorateOneTimePackage', checked);
                      }}
                      disabled={!studioProrationEnabled}
                    />
                    <Label htmlFor='prorateOneTimePackage'>
                      Prorate this package across the term
                    </Label>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Other Info Section */}
            <div className='space-y-4'>
              <h2 className='text-xl font-semibold'>Other Info</h2>
              <div className='space-y-2'>
                <Label>Tags</Label>
                <Controller
                  control={control}
                  name='otherInfo.tags'
                  render={({ field }) => (
                    <TagsSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <div className='space-y-2'>
                  <Label>Group</Label>
                  <Controller
                    control={control}
                    name='otherInfo.group'
                    render={({ field }) => (
                      <GroupsSelector
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
                <div className='space-y-2'>
                  <Label>Policy</Label>
                  <Controller
                    control={control}
                    name='otherInfo.policyGroup'
                    render={({ field }) => (
                      <PolicySelector
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  {typeof errors.otherInfo?.policyGroup?.message ===
                    'string' && (
                    <span className='text-sm text-destructive'>
                      {errors.otherInfo.policyGroup.message}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <Button
              type='submit'
              className='w-full'
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Updating Class
                </>
              ) : (
                'Update Class'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {unsavedChangesDialog}
    </div>
  );
};
